package fyi.moko.hotpl.data.image

/**
 * Local, offline store for user-picked place photos. Images live as files in an
 * app-private directory; a [fyi.moko.hotpl.domain.model.Place] only holds the file
 * name ([fyi.moko.hotpl.domain.model.Place.imageFileName]). Never leaves the device
 * and is not part of any backup.
 *
 * Platform implementations: `AndroidPlaceImageStore` (Context.filesDir) and
 * `IosPlaceImageStore` (Documents), registered in the platform Koin modules.
 */
interface PlaceImageStore {
    /**
     * Writes [bytes] as a new image file and returns its (directory-relative) file name.
     *
     * Throws rather than returning a name it could not back with a file -- every platform
     * now does: [ImageStoreFullException] when there is no room, the platform's own write
     * exception otherwise. Callers must catch, or the photo goes missing without a word.
     */
    suspend fun save(bytes: ByteArray): String

    /**
     * Writes [bytes] under an already-chosen [fileName] — the cache path for a photo fetched
     * from another device, whose name is fixed by the document that referenced it. Replaces
     * any existing file of that name.
     *
     * Defaulted to a no-op so stores that cannot (or need not) cache still satisfy the
     * interface; then a fetched photo is simply re-fetched next time.
     */
    suspend fun saveAs(fileName: String, bytes: ByteArray): Unit = Unit

    /** Reads the bytes for [fileName], or null if the file is missing. */
    suspend fun load(fileName: String): ByteArray?

    /** Deletes the file for [fileName]; a no-op if it doesn't exist. */
    suspend fun delete(fileName: String)

    /** Removes every stored image — used when a backup restore wipes all places. */
    suspend fun clear()
}

/**
 * 저장 공간이 없어 사진을 쓰지 못했다. [PlaceImageStore.save]가 던진다.
 *
 * 성공한 척하지 않기 위해 존재하는 타입이다. 웹 구현은 예전에 quota 예외를 삼키고 파일명을
 * 정상적으로 돌려줬는데, 그러면 장소는 사진 없이 저장되고 화면에는 아무 말도 뜨지 않아
 * 사용자가 사진을 잃은 걸 알 방법이 없었다. 던지는 쪽을 택했으니 **부르는 쪽이 반드시
 * 받아서 사용자에게 알려야 한다** — 안 받으면 저장 자체가 조용히 실패한다.
 */
class ImageStoreFullException(detail: String? = null) : Exception(
    "Storage is full, the photo was not saved" + (detail?.let { " ($it)" } ?: ""),
)
